import { useState, useCallback } from 'react'
import { commentsApi } from '../api/commentsApi'
import type { Comment } from '../types'

export function useComments(slug: string) {
  const [comments, setComments]   = useState<Comment[]>([])
  const [loading, setLoading]     = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError]         = useState<string | null>(null)

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const data = await commentsApi.getBySlug(slug)
      setComments(data)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load comments')
    } finally {
      setLoading(false)
    }
  }, [slug])

  const addComment = useCallback(
    async (body: string, parentCommentId?: string): Promise<boolean> => {
      setSubmitting(true)
      setError(null)
      try {
        const created = await commentsApi.create(slug, body, parentCommentId)
        if (parentCommentId) {
          // Append reply inside the parent
          setComments((prev) =>
            prev.map((c) =>
              c.id === parentCommentId
                ? { ...c, replies: [...c.replies, created] }
                : c,
            ),
          )
        } else {
          setComments((prev) => [...prev, created])
        }
        return true
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to post comment')
        return false
      } finally {
        setSubmitting(false)
      }
    },
    [slug],
  )

  const likeComment = useCallback(async (commentId: string) => {
    try {
      await commentsApi.like(commentId)
      const bump = (list: Comment[]): Comment[] =>
        list.map((c) => {
          if (c.id === commentId) return { ...c, likeCount: c.likeCount + 1 }
          if (c.replies.length) return { ...c, replies: bump(c.replies) }
          return c
        })
      setComments((prev) => bump(prev))
    } catch {
      // Silently ignore like errors (rate-limit etc.)
    }
  }, [])

  const deleteComment = useCallback(async (commentId: string) => {
    try {
      await commentsApi.delete(commentId)
      const remove = (list: Comment[]): Comment[] =>
        list
          .filter((c) => c.id !== commentId)
          .map((c) => ({ ...c, replies: remove(c.replies) }))
      setComments((prev) => remove(prev))
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete comment')
    }
  }, [])

  return { comments, loading, submitting, error, load, addComment, likeComment, deleteComment }
}
